/**
 * cypAdhdClinicalObservationConfig.js — the child-specific "Clinical
 * Observation (MSE)" for the CYP ADHD Clinical Interview
 * (cypAdhdFlow.mse === 'noObservationInSsi', WLC and TOC).
 *
 * Content is VERBATIM from Jasmine's specification (7 Oct 2026 version), with
 * the two changes she agreed in review: "Normal" is not a "No" option for
 * Speech & Language, and the Apraxia option's typo and bracket are corrected.
 * American spellings are kept as written.
 *
 * Later clinical decisions, recorded on the panel rather than by editing
 * the specification's words:
 *   - no "Unable to assess" answer (the third pill was removed);
 *   - Predominant emotional state is a Yes/No like the other Affect rows,
 *     with Euthymic as the Yes default and the other emotions as the No list;
 *   - Level of arousal is a Yes/No (Alert as the Yes default; Drowsy,
 *     Delirium, Stupor as the No list) instead of the spec's "Leave blank";
 *   - Mood is the first panel, and has no report-status question and no
 *     0-10 scale.
 *
 * Same subsection/category shape as cypAdhdMseConfig.js, so CypAdhdMsePanel
 * renders either and the stored mse shape is unchanged
 * (mse.{varName}.{categoryName} = { isAppropriateSelection, selectionDetails }).
 * detailsOnly categories have no Yes/No; the clinician only selects from
 * `options`. hint is a short line under the category name.
 */

export const CYP_CLINICAL_OBSERVATION_TITLE = 'Mental State Examination';

// The column reads "Age appropriate?". The question-mark tooltip carries the
// full definition, so the heading stays short enough for the decision column.
export const CYP_CLINICAL_OBSERVATION_DECISION_LABEL = 'Age appropriate?';

export const CYP_CLINICAL_OBSERVATION_DECISION_TOOLTIP =
  'Within expected range for age and developmental level?';

export const CYP_CLINICAL_OBSERVATION_INTRO = Object.freeze([
  'The Mental State Examination is a snapshot of the child or young person’s mental state during the assessment.',
  'Interpret findings in the context of their age and developmental level; and use the details field to select from the drop-down options or describe in your own words any clinically relevant findings.',
  'Use neutral, descriptive language and avoid interpreting intent.',
  'The Mental State Examination does not replace a fuller assessment of risk, diagnosis, history or functioning where these are clinically indicated.',
]);

export const CYP_CLINICAL_OBSERVATION_SUBSECTIONS = [
  {
    varName: 'mood',
    title: 'Mood',
    type: 'freeText',
    tooltipLines: [
      'Record the child/young person’s current mood at the time of the assessment, using their own words.',
      'Suggested question: "How would you describe your mood lately?"',
      'Longer term patterns should be explored in the clinical history.',
    ],
    freeTextLabel: 'Child/Young Person’s description of Mood',
  },
  {
    varName: 'appearance',
    title: 'Appearance',
    type: 'clickthrough',
    categories: [
      { name: 'Presentation', yesOptions: ['Appropriate for context'], noOptions: ['Disheveled', 'Unkempt', 'Unusual attire'] },
      { name: 'Hygiene', yesOptions: ['Appropriate'], noOptions: ['Reduced', 'Poor', 'Neglected'] },
      { name: 'Posture', yesOptions: ['Appropriate for context'], noOptions: ['Slouched', 'Slumped', 'Rigid', 'Unusual'] },
    ],
  },
  {
    varName: 'behaviour',
    title: 'Behaviour',
    type: 'clickthrough',
    categories: [
      { name: 'Interaction with clinician', yesOptions: ['Appropriate'], noOptions: ['Avoidant', 'Withdrawn', 'Guarded', 'Reluctant to engage', 'Overfamiliar', 'Agitated', 'Distressed', 'Signs of discomfort', 'Hostile', 'Other'] },
      { name: 'Interaction with parents / carers', yesOptions: ['Comfortable interaction'], noOptions: ['Clinging', 'Avoidant', 'Withdrawn', 'Defiant', 'Hostile', 'Distressed on separation', 'Distressed during interaction', 'Signs of discomfort'] },
      { name: 'Eye Contact', yesOptions: ['Appropriate to context'], noOptions: ['Limited', 'Avoidant', 'Variable', 'Staring'] },
      { name: 'Facial Expression', yesOptions: ['Appropriate to context'], noOptions: ['Limited', 'Reduced', 'Exaggerated', 'Blank', 'Incongruent'] },
      { name: 'Psychomotor Activity', yesOptions: ['Within expected range'], noOptions: ['Decreased', 'Agitated', 'Fidgety', 'Restless', 'Pacing', 'Increased'] },
      { name: 'Motor Patterns', yesOptions: ['No unusual movements'], noOptions: ['Unusual mannerisms', 'Repetitive movements', 'Stereotyped movements', 'Tics', 'Tremor', 'Other'] },
      { name: 'Gait & Coordination', yesOptions: ['Steady and coordinated'], noOptions: ['Slow or halting', 'Unsteady', 'Poor balance', 'Uncoordinated', 'Shuffling', 'Other'] },
    ],
  },
  {
    varName: 'speech',
    title: 'Speech',
    type: 'clickthrough',
    tooltipText: 'Observe the child/young person’s mechanics and characteristics of speech. Consider any communication or language differences.',
    categories: [
      { name: 'Rate', yesOptions: ['Appropriate'], noOptions: ['Rapid', 'Slow', 'Pressured', 'Deliberate'] },
      { name: 'Volume', yesOptions: ['Audible'], noOptions: ['Loud', 'Soft', 'Mute'] },
      { name: 'Tone', yesOptions: ['Appropriate'], noOptions: ['Monotone', 'High-pitched', 'Unusual'] },
      { name: 'Rhythm', yesOptions: ['Fluent with variation'], noOptions: ['Monotonous', 'Staccato', 'Other'] },
      { name: 'Fluency', yesOptions: ['Fluent'], noOptions: ['Halting', 'Word-finding difficulty', 'Pressured', 'Paucity of speech', 'Garrulous', 'Overly-talkative', 'Mute'] },
      { name: 'Quantity', yesOptions: ['Spontaneous and productive'], noOptions: ['Minimal', 'Detailed or elaborate', 'Excessive'] },
      { name: 'Quality', yesOptions: ['Clear'], noOptions: ['Stuttered', 'Slurred', 'Mumbled', 'Articulation errors'] },
      { name: 'Spontaneity', yesOptions: ['Spontaneous'], noOptions: ['Requires prompting', 'Minimal spontaneous speech'] },
      { name: 'Speech & Language', yesOptions: ['Within expected range'], noOptions: ['Neologisms (makes up new words)', 'Anomia (difficulty naming objects)', 'Apraxia (difficulty carrying out sequential speech)', 'Clang associations (meaningless word rhymes)', 'Echolalia (word echoing)', 'Incoherent', 'Aphasia (impairment of language)'] },
    ],
  },
  {
    varName: 'affect',
    title: 'Affect',
    type: 'clickthrough',
    tooltipText: 'Record the child/young person’s emotional expression during the assessment. Unlike Mood (what the patient says), Affect is what is observed.',
    categories: [
      // Euthymic is the within-expected default (it fills the details dropdown
      // on Yes). The other emotions are the No list.
      { name: 'Predominant emotional state', yesOptions: ['Euthymic'], noOptions: ['Happy', 'Angry', 'Anxious', 'Apathetic', 'Apprehensive', 'Dysphoric', 'Euphoric', 'Irritated', 'Sad'] },
      { name: 'Appropriateness with Described Mood', yesOptions: ['Congruent'], noOptions: ['Incongruent'] },
      { name: 'Range of Emotional Expression', yesOptions: ['Full'], noOptions: ['Constricted', 'Labile'] },
      { name: 'Reactivity', yesOptions: ['Reactive'], noOptions: ['Blunted', 'Flat'] },
    ],
  },
  {
    varName: 'thought',
    title: 'Thought',
    type: 'clickthrough',
    categories: [
      { name: 'Stream (speed)', yesOptions: ['Within expected range'], noOptions: ['Poverty of thought (thought blocking)', 'Poverty of content (perseveration)', 'Racing thoughts', 'Flight of ideas'] },
      { name: 'Form', yesOptions: ['Logical and goal-directed'], noOptions: ['Circumstantial', 'Tangential', 'Derailment', 'Looseness of associations', 'Word salad'] },
      { name: 'Content', yesOptions: ['Within expected range'], noOptions: ['Obsessions', 'Compulsions', 'Ruminations', 'Overvalued ideations', 'Delusions', 'Phobias', 'Magical thinking not typical for developmental stage', 'Thoughts of harm to self or others'] },
    ],
  },
  {
    varName: 'perception',
    title: 'Perception',
    type: 'clickthrough',
    categories: [
      { name: 'Perception', yesOptions: ['No perceptual disturbance identified'], noOptions: ['Illusions', 'Hallucinations', 'Depersonalisation', 'Derealisation', 'Passivity phenomena'] },
    ],
  },
  {
    varName: 'cognition',
    title: 'Cognition',
    type: 'clickthrough',
    categories: [
      // Alert is the only within-expected-range arousal state; the rest
      // are the No list. Changed from the spec's "Leave blank" (details-only)
      // to a Yes/No, matching the rest of the MSE.
      { name: 'Level of arousal', yesOptions: ['Alert'], noOptions: ['Drowsy', 'Delirium', 'Stupor'] },
      { name: 'Attention', yesOptions: ['Sustained'], noOptions: ['Requires redirection', 'Requires questions or instructions to be repeated', 'Easily distracted'] },
      { name: 'Orientation', yesOptions: ['Fully oriented (time, place, person, situation)'], noOptions: ['Partially oriented (time, place, person, situation)', 'Disoriented'] },
      { name: 'Conceptualisation', yesOptions: ['Abstract and flexible thinking'], noOptions: ['Predominantly concrete or literal thinking', 'Difficulty with abstract concepts', 'Difficulty generalising concepts', 'Difficulty with abstract reasoning'] },
      { name: 'Memory: Immediate', hint: 'eg repeat numbers, names back', yesOptions: ['No significant concern'], noOptions: ['Impaired'] },
      { name: 'Memory: Short-term', hint: 'eg recall three objects at 2 and 5 minutes', yesOptions: ['No significant concern'], noOptions: ['Impaired'] },
      { name: 'Memory: Long-term', hint: 'eg recall events of past week', yesOptions: ['No significant concern'], noOptions: ['Impaired'] },
    ],
  },
  {
    varName: 'insightAndJudgment',
    title: 'Insight and Judgment',
    type: 'clickthrough',
    categories: [
      { name: 'Insight', yesOptions: ['No significant concern'], noOptions: ['Partial', 'Poor'] },
      { name: 'Judgment', yesOptions: ['No significant concern'], noOptions: ['Impaired'] },
    ],
  },
];
